import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  createStoreChatSession,
  getStoreChatSessionChangeRequest,
  getStoreChatSessionChangeRequests,
  getStoreChatSessionMessages,
  getStoreChatSessions,
  postApproveStoreChatSessionChangeRequest,
  postStoreChatSessionMessage,
} from "../api/storeChatSessionsApi";

export const storeChatSessionsQueryKey = (storeId: StoreId) =>
  ["storeChatSessions", storeId] as const;

export const storeChatSessionMessagesQueryKey = (storeId: StoreId, chatSessionId: string) =>
  ["storeChatSessions", storeId, "messages", chatSessionId] as const;

export const storeChatSessionChangeRequestQueryKey = (storeId: StoreId, chatSessionId: string) =>
  ["storeChatSessions", storeId, "changeRequest", chatSessionId] as const;

export const storeChatSessionChangeRequestHistoryQueryKey = (storeId: StoreId, chatSessionId: string) =>
  [...storeChatSessionChangeRequestQueryKey(storeId, chatSessionId), "history"] as const;

export const useGetStoreChatSessionsQuery = (storeId: StoreId | undefined) => {
  return useQuery({
    queryKey: storeChatSessionsQueryKey(storeId!),
    queryFn: () => getStoreChatSessions(storeId!),
    enabled: !!storeId,
  });
};

export const useGetStoreChatSessionMessagesQuery = (
  storeId: StoreId | undefined,
  chatSessionId: string | null,
) => {
  return useQuery({
    queryKey: storeChatSessionMessagesQueryKey(storeId!, chatSessionId!),
    queryFn: () => getStoreChatSessionMessages(storeId!, chatSessionId!),
    enabled: !!storeId && !!chatSessionId,
  });
};

export const useGetStoreChatSessionChangeRequestQuery = (
  storeId: StoreId | undefined,
  chatSessionId: string | null,
) => {
  return useQuery({
    queryKey: storeChatSessionChangeRequestQueryKey(storeId!, chatSessionId!),
    queryFn: () => getStoreChatSessionChangeRequest(storeId!, chatSessionId!),
    enabled: !!storeId && !!chatSessionId,
  });
};

export const useGetStoreChatSessionChangeRequestHistoryQuery = (
  storeId: StoreId | undefined,
  chatSessionId: string | null,
) => {
  return useQuery({
    queryKey: storeChatSessionChangeRequestHistoryQueryKey(storeId!, chatSessionId!),
    queryFn: () => getStoreChatSessionChangeRequests(storeId!, chatSessionId!),
    enabled: !!storeId && !!chatSessionId,
  });
};

export const useCreateStoreChatSessionMutation = (storeId: StoreId | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ message, files }: { message: string; files?: File[] }) => {
      if (!storeId) {
        return Promise.reject(new Error("No store"));
      }
      return createStoreChatSession(storeId, message, files);
    },
    onSuccess: (session) => {
      if (storeId) {
        queryClient.invalidateQueries({ queryKey: storeChatSessionsQueryKey(storeId) });
        queryClient.invalidateQueries({
          queryKey: storeChatSessionMessagesQueryKey(storeId, session.id),
        });
        queryClient.invalidateQueries({
          queryKey: storeChatSessionChangeRequestQueryKey(storeId, session.id),
        });
      }
    },
  });
};

export const useSendStoreChatSessionMessageMutation = (
  storeId: StoreId | undefined,
  chatSessionId: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ message, files }: { message: string; files?: File[] }) => {
      if (!storeId || !chatSessionId) {
        return Promise.reject(new Error("No store or session"));
      }
      return postStoreChatSessionMessage(storeId, chatSessionId, message, files);
    },
    onSuccess: () => {
      if (storeId && chatSessionId) {
        queryClient.invalidateQueries({
          queryKey: storeChatSessionMessagesQueryKey(storeId, chatSessionId),
        });
        queryClient.invalidateQueries({
          queryKey: storeChatSessionChangeRequestQueryKey(storeId, chatSessionId),
        });
        queryClient.invalidateQueries({ queryKey: storeChatSessionsQueryKey(storeId) });
      }
    },
  });
};

export const useApproveStoreChatSessionChangeRequestMutation = (
  storeId: StoreId | undefined,
  chatSessionId: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!storeId || !chatSessionId) {
        return Promise.reject(new Error("No store or session"));
      }
      return postApproveStoreChatSessionChangeRequest(storeId, chatSessionId);
    },
    onSettled: () => {
      if (storeId && chatSessionId) {
        queryClient.invalidateQueries({
          queryKey: storeChatSessionChangeRequestQueryKey(storeId, chatSessionId),
        });
      }
    },
  });
};
